<template>
  <div class="home detail-actions-page">
    <div>
      <!-- Header -->
      <section> 
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between detail-page-header detail-page-title-row">
            <div class="col-12 col-lg-auto mb-4 mb-lg-0">
              
              <button type="button" class="btn btn-link text-decoration-none mb-2 detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
                <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
              </button> 
              <br/>
              <div class="d-flex align-items-center">
                 
                <h4 class="mb-0 me-2 detail-page-title">{{bankAccount?.account_name}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center detail-page-actions">
              <button type="button" @click="openUploadModal" class="btn btn-sm btn-success">
                <AppIcon name="bi-upload" class="bi bi-upload me-2" />
                {{$t('upload_transactions')}}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="px-8">
        <!-- Account Info Card -->
        <div class="card mb-4 shadow">
          <div class="card-body">
            <div class="row">
              <div class="col-md-2">
                <h6 class="text-muted mb-1">{{$t('bank_name')}}</h6>
                <p class="mb-0"><strong>{{bankAccount?.bank_name}}</strong></p>
              </div>
              <div class="col-md-2">
                <h6 class="text-muted mb-1">{{$t('account_number')}}</h6>
                <p class="mb-0"><strong>{{bankAccount?.account_number}}</strong></p>
              </div>
              <div class="col-md-2">
                <h6 class="text-muted mb-1">{{$t('currency')}}</h6>
                <p class="mb-0"><strong>{{bankAccount?.currency}}</strong></p>
              </div>
              <div class="col-md-2">
                <h6 class="text-muted mb-1">{{$t('balance')}}</h6>
                <p class="mb-0"><strong class="text-primary">{{formatMoney(bankAccount?.balance)}}</strong></p>
              </div>
              <div class="col-md-2">
                <h6 class="text-muted mb-1">{{$t('status')}}</h6>
                <p class="mb-0">
                  <span :class="bankAccount?.status === 'active' ? 'badge bg-success' : 'badge bg-secondary'">
                    {{$t(bankAccount?.status)}}
                  </span>
                </p>
              </div>
              <div class="col-md-2" v-if="lastUploadDate">
                <h6 class="text-muted mb-1">{{$t('last_upload')}}</h6>
                <p class="mb-0">
                  <AppIcon name="bi-clock-history" class="bi bi-clock-history me-1" />
                  <strong>{{formatDateTime(lastUploadDate)}}</strong>
                </p>
              </div>
            </div>
            <div class="row mt-3" v-if="bankAccount?.iban || bankAccount?.swift_code">
              <div class="col-md-6" v-if="bankAccount?.iban">
                <h6 class="text-muted mb-1">{{$t('iban')}}</h6>
                <p class="mb-0">{{bankAccount?.iban}}</p>
              </div>
              <div class="col-md-6" v-if="bankAccount?.swift_code">
                <h6 class="text-muted mb-1">{{$t('swift_code')}}</h6>
                <p class="mb-0">{{bankAccount?.swift_code}}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-3">
            <div class="card insight-card" style="height: 100%;">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('total_transactions')}}</small>
                    <h3 class="mb-0">{{stats?.total_transactions || 0}}</h3>
                  </div>
                  <div class="insight-icon bg-primary bg-opacity-10 text-primary">
                    <AppIcon name="bi-list-ul" class="bi bi-list-ul" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card insight-card" style="height: 100%;">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('incoming')}}</small>
                    <h3 class="mb-0">{{stats?.incoming_count || 0}}</h3>
                    <small class="text-success">{{formatMoney(stats?.incoming_sum || 0)}}</small>
                  </div>
                  <div class="insight-icon bg-success bg-opacity-10 text-success">
                    <AppIcon name="bi-arrow-down-circle" class="bi bi-arrow-down-circle" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card insight-card" style="height: 100%;">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('outgoing')}}</small>
                    <h3 class="mb-0">{{stats?.outgoing_count || 0}}</h3>
                    <small class="text-danger">{{formatMoney(stats?.outgoing_sum || 0)}}</small>
                  </div>
                  <div class="insight-icon bg-danger bg-opacity-10 text-danger">
                    <AppIcon name="bi-arrow-up-circle" class="bi bi-arrow-up-circle" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card insight-card" style="height: 100%;">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">{{$t('net_flow')}}</small>
                    <h3 class="mb-0">{{formatMoney(stats?.net_flow || 0)}}</h3>
                  </div>
                  <div :class="'insight-icon bg-' + ((stats?.net_flow || 0) >= 0 ? 'success' : 'danger') + ' bg-opacity-10 text-' + ((stats?.net_flow || 0) >= 0 ? 'success' : 'danger')">
                    <AppIcon :name="(stats?.net_flow || 0) >= 0 ? 'bi-graph-up' : 'bi-graph-down'" class="bi" :class="(stats?.net_flow || 0) >= 0 ? 'bi-graph-up' : 'bi-graph-down'" style="font-size: 24px;" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="card mb-4 shadow">
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <label class="form-label">{{$t('type')}}</label>
                <select v-model="type" class="form-select">
                  <option value="all">{{$t('all')}}</option>
                  <option value="incoming">{{$t('incoming')}}</option>
                  <option value="outgoing">{{$t('outgoing')}}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">{{$t('date_range')}}</label>
                <date-picker :clearable="true" :editable="false" v-model="range" type="date" :shortcuts="shortcuts" range :placeholder="$t('select_date_range')"></date-picker>
              </div>
              <div class="col-md-4">
                <label class="form-label">{{$t('search')}}</label>
                <input class="form-control" autocomplete="off" type="text" v-model="keyword" :placeholder="$t('search')">
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <button @click="applyFilters()" class="btn btn-primary w-100">
                  <AppIcon name="bi-search" class="bi bi-search me-2" />{{$t('filter')}}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Transactions Table -->
        <div class="page-table-container mb-6 bg-white rounded shadow">
          <div class="table-responsive">
            <table class="table mb-0 table-borderless table-striped small">
              <thead>
                <tr class="text-secondary">
                  <th class="pt-4 pb-3 px-6">{{$t('date')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('reference')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('description')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('category')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('type')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('amount')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('balance_after')}}</th>
                  <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transaction in transactions" :key="transaction.id">
                  <td class="py-5 px-6">{{formatDate(transaction.transaction_date)}}</td>
                  <td class="py-5 px-6">{{transaction.reference}}</td>
                  <td class="py-5 px-6">{{transaction.description}}</td>
                  <td class="py-5 px-6">{{transaction.category}}</td>
                  <td class="py-5 px-6">
                    <span :class="transaction.type === 'incoming' ? 'badge bg-success' : 'badge bg-danger'">
                      {{$t(transaction.type)}}
                    </span>
                  </td>
                  <td class="py-5 px-6">
                    <span :class="transaction.type === 'incoming' ? 'text-success' : 'text-danger'">
                      {{transaction.type === 'incoming' ? '+' : '-'}} {{formatMoney(transaction.amount)}}
                    </span>
                  </td>
                  <td class="py-5 px-6">{{formatMoney(transaction.balance_after)}}</td>
                  <td class="py-5 px-6">
                    <button @click="deleteTransaction(transaction)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                      <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                      {{$t('delete')}}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="count > transactions.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="page" @change="getTransactions" />
            </div>
          </div>

          <table-loading-bar v-if="loading" class="page-table-state text-center" />
          <div v-if="loading === false && !transactions.length" class="page-table-state text-center" style="padding:1rem">{{$t('no_transactions_found')}}.</div>
        </div>

        <div class="col-sm-6">
          {{$t('show')}}
          <select v-model="per_page" class="form-select mb-3" name="Results" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          {{$t('entities')}}
        </div>
      </section>
    </div>

    <!-- Upload Transactions Modal -->
    <upload-bank-transactions-modal 
      v-if="uploadModalOpen" 
      @modalClosed="uploadModalOpen = false"
      :isModalOpen="uploadModalOpen" 
      :bankAccountId="bankAccount?.id"
      @refresh="refresh"
    ></upload-bank-transactions-modal>
  </div>
</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import UploadBankTransactionsModal from '../../components/bank-account/UploadBankTransactionsModal.vue'

export default {
  name: 'BankAccountDetails',
  persistedFilterState: {
    key: 'bank-account-transactions',
    routeParams: ['id'],
    fields: ['keyword', 'type', 'range', 'per_page'],
    dateFields: ['range'],
  },
  components: {
    DatePicker,
    UploadBankTransactionsModal
  },
  data() {
    return {
      bankAccount: null,
      transactions: [],
      stats: {},
      count: 0,
      page: 1,
      per_page: 20,
      type: 'all',
      keyword: '',
      range: null,
      loading: false,
      uploadModalOpen: false,
      lastUploadDate: null,
      shortcuts: [
        {
          text: this.$t('today'),
          onClick: () => {
            const today = new Date()
            this.range = [today, today]
          }
        },
        {
          text: this.$t('this_week'),
          onClick: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            this.range = [start, end]
          }
        },
        {
          text: this.$t('this_month'),
          onClick: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            this.range = [start, end]
          }
        }
      ],
      helper
    }
  },
  computed: {
    pagination() {
      const pages = Math.ceil(this.count / this.per_page)
      return Array.from({ length: pages }, (_, i) => i + 1)
    }
  },
  mounted() {
    window.mixpanel.track("bank_account_details");
    this.getBankAccount()
    this.getStats()
    this.getTransactions()
  },
  watch: {
    per_page() {
      this.page = 1
      this.getTransactions()
    }
  },
  methods: {
    async getBankAccount() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/bank-accounts/${this.$route.params.id}`)
        this.bankAccount = response.data
      } catch (error) {
        alert(error)
      }
    },
    async getStats() {
      try {
        let url = `${process.env.VUE_APP_API}/bank-accounts/${this.$route.params.id}/stats?`
        if (this.range && this.range.length === 2) {
          url += `start=${moment(this.range[0]).format('YYYY-MM-DD')}&end=${moment(this.range[1]).format('YYYY-MM-DD')}`
        }
        const response = await helper.customGet(url)
        this.stats = response.data
      } catch (error) {
        alert(error)
      }
    },
    async getTransactions(page = 1) {
      try {
        this.loading = true
        this.page = page
        let url = `${process.env.VUE_APP_API}/bank-transactions?page=${this.page}&per_page=${this.per_page}&bank_account_id=${this.$route.params.id}`
        
        if(this.type && this.type !== 'all'){
          url += `&type=${this.type}`
        }
        
        if(this.keyword){
          url += `&keyword=${this.keyword}`
        }
        
        if (this.range && this.range.length === 2) {
          url += `&start=${moment(this.range[0]).format('YYYY-MM-DD')}&end=${moment(this.range[1]).format('YYYY-MM-DD')}`
        }
        
        const response = await helper.customGet(url)
        this.transactions = response.data.data
        this.count = response.data.count
        
        // Get last upload date from most recent transaction
        if (this.transactions.length > 0) {
          const sortedTransactions = [...this.transactions].sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          )
          this.lastUploadDate = sortedTransactions[0].created_at
        }
        
        this.loading = false
      } catch (error) {
        this.loading = false
        alert(error)
      }
    },
    applyFilters() {
      this.page = 1
      this.getTransactions()
      this.getStats()
    },
    openUploadModal() {
      this.uploadModalOpen = true
    },
    async deleteTransaction(transaction) {
      if (!confirm(this.$t('confirm_delete'))) return
      
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/bank-transactions/${transaction.id}`, {showToast: true})
        this.transactions = helper.withoutDeletedRecords(this.transactions, transaction.id)
        this.count = Math.max(0, this.count - 1)
        this.getBankAccount()
        this.getStats()
      } catch (error) {
        alert(error)
      }
    },
    refresh() {
      this.getBankAccount()
      this.getStats()
      this.getTransactions()
    },
    formatMoney(amount) {
      return helper.formatMoney(amount)
    },
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD')
    },
    formatDateTime(date) {
      return moment(date).format('YYYY-MM-DD HH:mm')
    }
  }
}
</script>

<style scoped>
.insight-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: transform 0.2s;
}

.insight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.insight-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
